import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';
import { faFacebookSquare } from '@fortawesome/free-brands-svg-icons/faFacebookSquare';
import { faPinterestSquare } from '@fortawesome/free-brands-svg-icons/faPinterestSquare';
import { faTwitterSquare } from '@fortawesome/free-brands-svg-icons/faTwitterSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCurrentUrlFromState } from '@/redux/modules/route';
import { getImageUrl } from '@/utils/urls';
import { getItem } from '@/redux/modules/item';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

type ShareButton = {
    icon?: string[];
    network: string;
    shareUrl: Function;
};

const shareButtons: ShareButton[] = [
    {
        // @ts-ignore
        icon: faEnvelope,
        network: 'Email',
        shareUrl: (baseUrl, title) =>
            `mailto:?subject=I%20thought%20you%20might%20like%20this%20on%20LiveAuctioneers&body=${title}%20${baseUrl}`,
    },
    {
        // @ts-ignore
        icon: faPinterestSquare,
        network: 'Pinterest',
        shareUrl: (baseUrl, title, imgUrl) =>
            `https://pinterest.com/pin/create/button/?url=${baseUrl}&media=${imgUrl}&description=${title}`,
    },
    {
        // @ts-ignore
        icon: faTwitterSquare,
        network: 'Twitter',
        shareUrl: (baseUrl, title) => `https://twitter.com/intent/tweet/?text=${title}&url=${baseUrl}`,
    },
    {
        // @ts-ignore
        icon: faFacebookSquare,
        network: 'Facebook',
        shareUrl: (baseUrl) => `https://facebook.com/sharer/sharer.php?u=${baseUrl}`,
    },
];

type Props = {
    itemId: number;
    recordSocialNetworkShare: Function;
};

const SocialIcons = ({ itemId, recordSocialNetworkShare }: Props) => {
    const { catalogId, imageVersion, photos, sellerId, title: itemTitle } = useSelector(
        (state) => getItem(state, itemId),
        isEqual
    );

    const itemUrl = useSelector((state) => getCurrentUrlFromState(state), isEqual);

    const itemImageUrl =
        photos.length > 0
            ? getImageUrl({
                  catalogId,
                  imageNumber: photos[0],
                  imageVersion,
                  itemId,
                  sellerId,
                  width: 375,
              })
            : '';

    const track = (network: string) => {
        recordSocialNetworkShare(network, itemUrl);
    };

    const escapedTitle = encodeURIComponent(itemTitle);
    const escapedBaseUrl = encodeURIComponent(itemUrl);
    const escapedItemImageUrl = encodeURIComponent(itemImageUrl);

    return (
        <SocialIconsDiv>
            {shareButtons.map(({ icon, network, shareUrl }) => (
                <StyledText key={network}>
                    <StyledLink
                        // @ts-ignore
                        alt={network}
                        aria-label={network}
                        href={shareUrl(escapedBaseUrl, escapedTitle, escapedItemImageUrl)}
                        id={`share-${network.toLowerCase()}`}
                        key={network}
                        name={network}
                        onClick={() => track(network)}
                        rel="noopener"
                        target="_blank"
                    >
                        {/* @ts-ignore */}
                        <FontAwesomeIcon icon={icon || ['fab', network.toLowerCase()]} />
                    </StyledLink>
                </StyledText>
            ))}
        </SocialIconsDiv>
    );
};

export default React.memo<Props>(SocialIcons, isEqual);

const SocialIconsDiv = styled.div`
    display: flex;
    justify-content: center;
    border: 0;

    @media print {
        display: none;
    }
`;

const StyledText = styled.div`
    width: 40px;
    padding-right: 0;
    margin-right: 0;
    background-color: white;
`;

const StyledLink = styled(Link)`
    &&& {
        display: inline-block;
        text-decoration: none;
        color: ${({ theme }) => theme.colors.steel};
        font-size: 28px;
        margin-left: 0.5em;
        background-color: white;
        &:hover {
            color: ${({ theme }) => theme.colors.blue200};
        }

        &:first-child {
            margin-left: 0 !important;
        }
    }
`;

StyledText.displayName = 'StyledText';
